import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
    ColDef,
    GridApi,
    ModuleRegistry,
    ClientSideRowModelModule,
    ValidationModule,
    QuickFilterModule,
    PaginationModule,
    RowStyleModule,
    RowAutoHeightModule,
    ExternalFilterModule,
    RenderApiModule,
    DateEditorModule,
    ICellRendererParams,
} from 'ag-grid-community';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Snackbar,
    Alert,
    IconButton,
    useTheme,
    alpha,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Checkbox,
    Tabs,
    Tab,
} from '@mui/material';
import {
    Save as SaveIcon,
    Lock as LockIcon,
    Info as InfoIcon,
    PersonAdd as PersonAddIcon,
    PersonRemove as PersonRemoveIcon,
    Close as CloseIcon,
    ChevronRight as ChevronRightIcon,
    ExpandMore as ExpandMoreIcon,
    UnfoldMore as UnfoldMoreIcon,
    UnfoldLess as UnfoldLessIcon,
} from '@mui/icons-material';
import featuresService from '../services/featuresService';
import { FeaturePermissionMatrix, PermissionUpdate, PermissionPreview } from '../types';
import { useTabSearchParam } from '../hooks/useTabSearchParam';
import { ClearGridFiltersButton, useColumnFiltersActive } from '../components/ClearGridFiltersButton';

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    ValidationModule,
    QuickFilterModule,
    PaginationModule,
    RowStyleModule,
    RowAutoHeightModule,
    ExternalFilterModule,
    RenderApiModule,
    DateEditorModule,
]);

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b', abyssal: '#0a1622' };

/** Measure label width so permission columns fit their header text. */
const measureTextWidth = (text: string, font: string): number => {
    if (typeof document === 'undefined') return text.length * 8;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return text.length * 8;
    ctx.font = font;
    return Math.ceil(ctx.measureText(text).width);
};

const colWidthForLabel = (
    label: string,
    opts: { min?: number; max?: number; pad?: number; font?: string } = {},
): number => {
    const {
        min = 64,
        max = 240,
        pad = 32,
        font = '600 11px Roboto, Helvetica, Arial, sans-serif',
    } = opts;
    const base = Math.min(max, Math.max(min, measureTextWidth(label, font) + pad));
    return Math.round(base * 1.4);
};

interface PermissionRow {
    id: string;
    type: 'category' | 'feature';
    categoryId: number | null;
    featureId: number | null;
    name: string;
    kind?: 'page' | 'action';
    [key: string]: any;
}

const FeaturePermissionsPage: React.FC = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
    const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';
    const headerBg = isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.04);
    const ink = isDark ? '#ffffff' : NAUTILUS.deepOcean;
    const inkSecondary = isDark ? '#b0bec5' : '#546e7a';
    const categoryRowBg = isDark ? alpha(NAUTILUS.sand, 0.06) : alpha(NAUTILUS.deepOcean, 0.04);
    const categoryRoleCellBg = isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.sand, 0.12);

    const technicalLabelSx = useMemo(
        () => ({
            fontSize: '0.65rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase' as const,
            color: inkSecondary,
        }),
        [inkSecondary],
    );

    const outlineBtnSx = {
        borderRadius: 1,
        borderColor: panelBorder,
        color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        fontWeight: 600,
        fontSize: '0.75rem',
        boxShadow: 'none',
        '&:hover': {
            borderColor: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
            bgcolor: isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.04),
            boxShadow: 'none',
        },
    };

    const primaryBtnSx = {
        borderRadius: 1,
        fontWeight: 600,
        boxShadow: 'none',
        bgcolor: NAUTILUS.sand,
        color: NAUTILUS.deepOcean,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        fontSize: '0.75rem',
        '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' },
        '&.Mui-disabled': {
            bgcolor: alpha(NAUTILUS.sand, 0.35),
            color: alpha(NAUTILUS.deepOcean, 0.5),
        },
    };

    const gridChromeSx = {
        flex: 1,
        minHeight: 0,
        height: '100%',
        width: '100%',
        '--ag-background-color': surfaceBg,
        '--ag-header-background-color': headerBg,
        '--ag-odd-row-background-color': isDark
            ? alpha(NAUTILUS.sand, 0.03)
            : alpha(NAUTILUS.deepOcean, 0.015),
        '--ag-row-hover-color': isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.04),
        '--ag-border-color': panelBorder,
        '--ag-header-foreground-color': isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
        '--ag-foreground-color': ink,
        '--ag-secondary-foreground-color': inkSecondary,
        '& .ag-header': {
            borderBottom: `1px solid ${panelBorder}`,
        },
        '& .ag-header-cell-text': {
            fontSize: '0.7rem',
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
        },
        '& .ag-root-wrapper': {
            border: 'none',
            height: '100%',
        },
        '& .category-role-header': {
            backgroundColor: isDark ? alpha(NAUTILUS.sand, 0.1) : alpha(NAUTILUS.sand, 0.18),
        },
        '& .job-role-header': {
            backgroundColor: headerBg,
        },
    };

    const [activeTab, setActiveTab] = useTabSearchParam(
        ['by-role', 'by-user'] as const,
        'by-role',
    );
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState<FeaturePermissionMatrix | null>(null);
    const [updates, setUpdates] = useState<PermissionUpdate[]>([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState<PermissionPreview | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    const [roleGridApi, setRoleGridApi] = useState<GridApi | null>(null);
    const [userGridApi, setUserGridApi] = useState<GridApi | null>(null);
    const roleFilters = useColumnFiltersActive();
    const userFilters = useColumnFiltersActive();

    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error';
    }>({ open: false, message: '', severity: 'success' });

    const [expandedCategoryIds, setExpandedCategoryIds] = useState<number[]>([]);

    const loadData = async () => {
        setLoading(true);
        try {
            const result = await featuresService.getPermissions();
            setData(result);
            setUpdates([]);
            if (result.categories) {
                setExpandedCategoryIds(result.categories.map((c) => c.id));
            }
        } catch (error) {
            console.error(error);
            setSnackbar({ open: true, message: 'Failed to load permissions', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const getPermissionValue = useCallback(
        (featureId: number, type: 'role' | 'user', targetId: number): boolean => {
            if (!data) return false;

            const update = updates.find(
                (u) => u.feature_id === featureId && u.type === type && u.target_id === targetId,
            );
            if (update) return update.allowed;

            if (type === 'role') {
                const perm = data.role_permissions.find(
                    (p) => p.feature_id === featureId && p.role_id === targetId,
                );
                return perm ? perm.allowed : false;
            }
            const perm = data.user_permissions.find(
                (p) => p.feature_id === featureId && p.user_id === targetId,
            );
            return perm ? perm.allowed : false;
        },
        [data, updates],
    );

    const getCategoryPermissionStatus = useCallback(
        (categoryId: number, type: 'role' | 'user', targetId: number): 'full' | 'partial' | 'none' => {
            if (!data) return 'none';

            const categoryFeatures = data.features.filter((f) => f.category_id === categoryId);
            if (categoryFeatures.length === 0) return 'none';

            const assignedCount = categoryFeatures.filter((f) =>
                getPermissionValue(f.id, type, targetId),
            ).length;

            if (assignedCount === 0) return 'none';
            if (assignedCount === categoryFeatures.length) return 'full';
            return 'partial';
        },
        [data, getPermissionValue],
    );

    const toggleCategoryPermission = useCallback(
        (categoryId: number, type: 'role' | 'user', targetId: number) => {
            if (!data) return;

            const currentStatus = getCategoryPermissionStatus(categoryId, type, targetId);
            const categoryFeatures = data.features.filter((f) => f.category_id === categoryId);
            const newValue = currentStatus !== 'full';

            const newUpdates: PermissionUpdate[] = categoryFeatures.map((f) => ({
                feature_id: f.id,
                type,
                target_id: targetId,
                allowed: newValue,
            }));

            setUpdates((prev) => {
                const filtered = prev.filter(
                    (u) =>
                        !(
                            categoryFeatures.some((f) => f.id === u.feature_id) &&
                            u.type === type &&
                            u.target_id === targetId
                        ),
                );
                return [...filtered, ...newUpdates];
            });

            setTimeout(() => {
                roleGridApi?.refreshCells();
                userGridApi?.refreshCells();
            }, 0);
        },
        [data, getCategoryPermissionStatus, roleGridApi, userGridApi],
    );

    const rowData = useMemo(() => {
        if (!data) return [];
        const rows: PermissionRow[] = [];

        const sortedCategories = [...(data.categories || [])].sort(
            (a, b) => a.display_order - b.display_order,
        );

        sortedCategories.forEach((category) => {
            rows.push({
                id: `cat-${category.id}`,
                type: 'category',
                categoryId: category.id,
                featureId: null,
                name: category.name,
            });

            const categoryFeatures = data.features
                .filter((f) => f.category_id === category.id)
                .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

            categoryFeatures.forEach((feature) => {
                const row: PermissionRow = {
                    id: `feat-${feature.id}`,
                    type: 'feature',
                    categoryId: category.id,
                    featureId: feature.id,
                    name: feature.name,
                    kind: feature.kind || 'page',
                };

                data.roles.forEach((role) => {
                    row[`role_${role.id}`] = getPermissionValue(feature.id, 'role', role.id);
                });

                data.users.forEach((user) => {
                    row[`user_${user.id}`] = getPermissionValue(feature.id, 'user', user.id);
                });

                rows.push(row);
            });
        });

        const uncategorizedFeatures = data.features.filter((f) => !f.category_id);
        if (uncategorizedFeatures.length > 0) {
            rows.push({
                id: 'cat-uncategorized',
                type: 'category',
                categoryId: -1,
                featureId: null,
                name: 'Uncategorized',
            });

            uncategorizedFeatures.forEach((feature) => {
                const row: PermissionRow = {
                    id: `feat-${feature.id}`,
                    type: 'feature',
                    categoryId: -1,
                    featureId: feature.id,
                    name: feature.name,
                    kind: feature.kind || 'page',
                };

                data.roles.forEach((role) => {
                    row[`role_${role.id}`] = getPermissionValue(feature.id, 'role', role.id);
                });

                data.users.forEach((user) => {
                    row[`user_${user.id}`] = getPermissionValue(feature.id, 'user', user.id);
                });

                rows.push(row);
            });
        }

        return rows;
    }, [data, getPermissionValue]);

    const featureColWidth = useMemo(() => {
        if (!data) return 280;
        const labels = [
            'Feature',
            ...(data.categories || []).map((c) => c.name),
            ...data.features.map((f) =>
                f.kind === 'action' ? `${f.name}  ACTION` : f.name,
            ),
        ];
        const longest = labels.reduce((a, b) => (a.length >= b.length ? a : b), 'Feature');
        // Extra pad for expand chevron, indent, and Action badge
        return colWidthForLabel(longest, {
            min: 220,
            max: 440,
            pad: 96,
            font: '600 13px Roboto, Helvetica, Arial, sans-serif',
        });
    }, [data]);

    const { jobRoles, categoryRoles } = useMemo(() => {
        if (!data) return { jobRoles: [], categoryRoles: [] };
        return {
            jobRoles: data.roles.filter((r) => r.group === 'job'),
            categoryRoles: data.roles.filter((r) => r.group === 'category'),
        };
    }, [data]);

    const toggleCategory = (categoryId: number) => {
        setExpandedCategoryIds((prev) =>
            prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId],
        );
        setTimeout(() => roleGridApi?.onFilterChanged(), 0);
        setTimeout(() => userGridApi?.onFilterChanged(), 0);
    };

    const expandAll = () => {
        if (data?.categories) {
            setExpandedCategoryIds([...data.categories.map((c) => c.id), -1]);
        }
    };

    const collapseAll = () => {
        setExpandedCategoryIds([]);
    };

    const isExternalFilterPresent = useCallback(() => true, []);

    const doesExternalFilterPass = useCallback(
        (node: any) => {
            const row = node.data as PermissionRow;
            if (row.type === 'category') return true;
            if (row.type === 'feature') {
                return expandedCategoryIds.includes(row.categoryId!);
            }
            return true;
        },
        [expandedCategoryIds],
    );

    const CategoryCellRenderer = useCallback(
        (params: ICellRendererParams) => {
            const { data: row } = params;
            if (row.type === 'category') {
                const isExpanded = expandedCategoryIds.includes(row.categoryId);
                const hasChildren = rowData.some(
                    (r) => r.type === 'feature' && r.categoryId === row.categoryId,
                );

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {hasChildren && (
                            <IconButton
                                size="small"
                                onClick={() => toggleCategory(row.categoryId)}
                                sx={{
                                    p: 0.5,
                                    mr: 1,
                                    color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                                }}
                            >
                                {isExpanded ? (
                                    <ExpandMoreIcon fontSize="small" />
                                ) : (
                                    <ChevronRightIcon fontSize="small" />
                                )}
                            </IconButton>
                        )}
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 700,
                                letterSpacing: '-0.01em',
                                color: ink,
                                fontSize: '0.8rem',
                            }}
                        >
                            {row.name}
                        </Typography>
                    </Box>
                );
            }

            return (
                <Box sx={{ pl: 4, display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <Typography variant="body2" sx={{ color: ink, fontSize: '0.8125rem' }}>
                        {row.name}
                    </Typography>
                    {row.kind === 'action' && (
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-block',
                                px: 0.75,
                                py: 0.125,
                                borderRadius: 0.5,
                                border: `1px solid ${panelBorder}`,
                                bgcolor: isDark
                                    ? alpha(NAUTILUS.sand, 0.1)
                                    : alpha(NAUTILUS.deepOcean, 0.04),
                                fontFamily: 'monospace',
                                fontWeight: 700,
                                fontSize: '0.65rem',
                                letterSpacing: '0.08em',
                                color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                                lineHeight: 1.4,
                            }}
                        >
                            ACTION
                        </Box>
                    )}
                </Box>
            );
        },
        [expandedCategoryIds, rowData, isDark, ink, panelBorder],
    );

    const createCategoryCheckboxRenderer = useCallback(
        (type: 'role' | 'user', targetId: number) => {
            return (params: ICellRendererParams) => {
                const { data: row } = params;

                if (row.type === 'category') {
                    const status = getCategoryPermissionStatus(row.categoryId, type, targetId);

                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                            }}
                        >
                            <Checkbox
                                checked={status === 'full'}
                                indeterminate={status === 'partial'}
                                onChange={() =>
                                    toggleCategoryPermission(row.categoryId, type, targetId)
                                }
                                size="small"
                                sx={{
                                    color:
                                        status === 'partial'
                                            ? NAUTILUS.sand
                                            : inkSecondary,
                                    '&.Mui-checked': {
                                        color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                                    },
                                    '&.MuiCheckbox-indeterminate': {
                                        color: NAUTILUS.sand,
                                    },
                                }}
                            />
                        </Box>
                    );
                }

                const value = params.value;
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                        }}
                    >
                        <Checkbox
                            checked={value}
                            onChange={(e) => {
                                params.setValue(e.target.checked);
                            }}
                            size="small"
                            sx={{
                                color: inkSecondary,
                                '&.Mui-checked': {
                                    color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                                },
                            }}
                        />
                    </Box>
                );
            };
        },
        [getCategoryPermissionStatus, toggleCategoryPermission, isDark, inkSecondary],
    );

    const roleColumnDefs = useMemo<ColDef[]>(() => {
        if (!data) return [];

        const columns: ColDef[] = [
            {
                field: 'name',
                headerName: 'Feature',
                pinned: 'left',
                width: featureColWidth,
                minWidth: 180,
                cellRenderer: CategoryCellRenderer,
            },
        ];

        jobRoles.forEach((role) => {
            const width = colWidthForLabel(role.name, { min: 72, max: 160, pad: 28 });
            columns.push({
                field: `role_${role.id}`,
                headerName: role.name,
                width,
                minWidth: 64,
                cellRenderer: createCategoryCheckboxRenderer('role', role.id),
                editable: false,
                headerClass: 'job-role-header',
                cellStyle: (params: any) =>
                    params.data.type === 'category' ? { backgroundColor: categoryRowBg } : {},
            });
        });

        categoryRoles.forEach((role) => {
            const width = colWidthForLabel(role.name, { min: 72, max: 160, pad: 28 });
            columns.push({
                field: `role_${role.id}`,
                headerName: role.name,
                width,
                minWidth: 64,
                cellRenderer: createCategoryCheckboxRenderer('role', role.id),
                editable: false,
                headerClass: 'category-role-header',
                cellStyle: (params: any) => ({
                    backgroundColor:
                        params.data.type === 'category' ? categoryRowBg : categoryRoleCellBg,
                }),
            });
        });

        return columns;
    }, [
        data,
        jobRoles,
        categoryRoles,
        featureColWidth,
        CategoryCellRenderer,
        createCategoryCheckboxRenderer,
        categoryRowBg,
        categoryRoleCellBg,
    ]);

    const userColumnDefs = useMemo<ColDef[]>(() => {
        if (!data) return [];
        return [
            {
                field: 'name',
                headerName: 'Feature',
                pinned: 'left',
                width: featureColWidth,
                minWidth: 180,
                cellRenderer: CategoryCellRenderer,
            },
            ...data.users.map((user) => {
                const width = colWidthForLabel(user.name, { min: 72, max: 180, pad: 28 });
                return {
                    field: `user_${user.id}`,
                    headerName: user.name,
                    width,
                    minWidth: 64,
                    cellRenderer: createCategoryCheckboxRenderer('user', user.id),
                    editable: false,
                    cellStyle: (params: any) =>
                        params.data.type === 'category' ? { backgroundColor: categoryRowBg } : {},
                };
            }),
        ];
    }, [
        data,
        featureColWidth,
        CategoryCellRenderer,
        createCategoryCheckboxRenderer,
        categoryRowBg,
    ]);

    const handleCellValueChanged = async (params: any) => {
        if (params.data.type === 'category') return;

        const field = params.colDef.field;
        const [type, targetIdStr] = field.split('_');
        const targetId = parseInt(targetIdStr);
        const featureId = params.data.featureId;
        const allowed: boolean = params.newValue;

        const distinctUpdate: PermissionUpdate = {
            feature_id: featureId,
            type: type as 'role' | 'user',
            target_id: targetId,
            allowed,
        };

        setUpdates((prev) => {
            const filtered = prev.filter(
                (u) => !(u.feature_id === featureId && u.type === type && u.target_id === targetId),
            );
            return [...filtered, distinctUpdate];
        });

        if (type === 'role') {
            setPreviewLoading(true);
            try {
                const preview = await featuresService.previewPermissionChange(
                    featureId,
                    targetId,
                    allowed,
                );
                if (preview.affected_users && preview.affected_users.length > 0) {
                    setPreviewData(preview);
                    setPreviewOpen(true);
                }
            } catch (error) {
                console.error('Failed to load preview:', error);
            } finally {
                setPreviewLoading(false);
            }
        }
    };

    const handleSave = async () => {
        if (updates.length === 0) return;
        setSaving(true);
        try {
            await featuresService.updatePermissions(updates);
            setSnackbar({
                open: true,
                message: 'Permissions saved successfully',
                severity: 'success',
            });
            loadData();
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to save permissions', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (roleGridApi) roleGridApi.onFilterChanged();
        if (userGridApi) userGridApi.onFilterChanged();
    }, [expandedCategoryIds, roleGridApi, userGridApi]);

    const roleCount = data?.roles.length ?? 0;
    const userCount = data?.users.length ?? 0;
    const featureCount = data?.features.length ?? 0;

    return (
        <Box
            sx={{
                p: 3,
                pb: 1,
                // Fill viewport: app bar (~64) + main top pad (24); cancel main bottom pad
                height: 'calc(100vh - 88px)',
                mb: -3,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            {/* Page Header */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                flexShrink={0}
                sx={{ pb: 1.5, borderBottom: `1px solid ${panelBorder}` }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LockIcon
                        sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, fontSize: '1.75rem' }}
                    />
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            fontWeight: 600,
                            letterSpacing: '-0.01em',
                            color: ink,
                        }}
                    >
                        Feature Access
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                        variant="outlined"
                        startIcon={<UnfoldMoreIcon />}
                        onClick={expandAll}
                        size="small"
                        sx={outlineBtnSx}
                    >
                        Expand All
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<UnfoldLessIcon />}
                        onClick={collapseAll}
                        size="small"
                        sx={outlineBtnSx}
                    >
                        Collapse All
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={updates.length === 0 || saving}
                        onClick={handleSave}
                        sx={primaryBtnSx}
                    >
                        {saving
                            ? 'Saving...'
                            : updates.length > 0
                              ? `Save Changes (${updates.length})`
                              : 'Save Changes'}
                    </Button>
                </Box>
            </Box>

            {/* Access formula — Text Intake Panel pattern */}
            <Box
                sx={{
                    mb: 2,
                    flexShrink: 0,
                    p: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${panelBorder}`,
                    bgcolor: isDark ? alpha(NAUTILUS.sand, 0.06) : alpha(NAUTILUS.deepOcean, 0.03),
                    borderLeft: `3px solid ${NAUTILUS.sand}`,
                }}
            >
                <Typography sx={{ ...technicalLabelSx, mb: 0.5 }}>Access formula</Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: ink,
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        fontSize: '0.8125rem',
                        letterSpacing: '0.02em',
                        mb: 0.75,
                    }}
                >
                    Access = (Job Role ✓) AND (Category Role ✓)
                </Typography>
                <Typography variant="body2" sx={{ color: inkSecondary, lineHeight: 1.5, maxWidth: 880, fontSize: '0.8rem' }}>
                    Users need permission from both a job role (PM, Eng, etc.) and a category role
                    (Intern/Extern). Category checkboxes: ✓ = all features, − = partial, ☐ = none.
                    Pages control menu/route access; Actions control in-app operations. Assigned
                    project managers (pm_id) may still perform some document actions on their projects.
                </Typography>
            </Box>

            {/* View tabs */}
            <Box
                sx={{
                    flexShrink: 0,
                    borderBottom: `1px solid ${panelBorder}`,
                    mb: 0,
                }}
            >
                <Tabs
                    value={activeTab}
                    onChange={(_, value: 'by-role' | 'by-user') => setActiveTab(value)}
                    sx={{
                        minHeight: 40,
                        '& .MuiTab-root': {
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            minHeight: 40,
                            color: inkSecondary,
                        },
                        '& .Mui-selected': {
                            color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                        },
                    }}
                >
                    <Tab label={`By Role (${roleCount})`} value="by-role" />
                    <Tab label={`By User (${userCount})`} value="by-user" />
                </Tabs>
            </Box>

            {/* Active matrix — fills remaining viewport */}
            <Card
                sx={{
                    flex: 1,
                    minHeight: 0,
                    mt: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: surfaceBg,
                    border: `1px solid ${panelBorder}`,
                    borderTop: 'none',
                    borderRadius: '0 0 4px 4px',
                    boxShadow: 'none',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        px: 2,
                        py: 1,
                        flexShrink: 0,
                        bgcolor: headerBg,
                        borderBottom: `1px solid ${panelBorder}`,
                    }}
                >
                    {activeTab === 'by-role' ? (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        letterSpacing: '-0.01em',
                                        color: ink,
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    Role Permissions
                                </Typography>
                                <Typography sx={{ ...technicalLabelSx, letterSpacing: '0.06em' }}>
                                    {roleCount} roles · {featureCount} features
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: 0.5,
                                            border: `1px solid ${panelBorder}`,
                                            bgcolor: headerBg,
                                        }}
                                    />
                                    <Typography sx={{ ...technicalLabelSx, fontSize: '0.6rem' }}>
                                        Job
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: 0.5,
                                            border: `1px solid ${panelBorder}`,
                                            bgcolor: isDark
                                                ? alpha(NAUTILUS.sand, 0.1)
                                                : alpha(NAUTILUS.sand, 0.18),
                                        }}
                                    />
                                    <Typography sx={{ ...technicalLabelSx, fontSize: '0.6rem' }}>
                                        Category
                                    </Typography>
                                </Box>
                                {roleFilters.columnFiltersActive && (
                                    <ClearGridFiltersButton onClick={() => roleFilters.clearColumnFilters(roleGridApi)} />
                                )}
                            </Box>
                        </>
                    ) : (
                        <>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        letterSpacing: '-0.01em',
                                        color: ink,
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    User Overrides
                                </Typography>
                                <Typography sx={{ ...technicalLabelSx, letterSpacing: '0.06em' }}>
                                    {userCount} users · {featureCount} features
                                </Typography>
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{ color: inkSecondary, mt: 0.25, fontSize: '0.75rem', lineHeight: 1.4 }}
                            >
                                Grant or deny specific users access beyond their role defaults.
                            </Typography>
                        </Box>
                        {userFilters.columnFiltersActive && (
                            <ClearGridFiltersButton onClick={() => userFilters.clearColumnFilters(userGridApi)} />
                        )}
                        </>
                    )}
                </Box>
                <CardContent
                    sx={{
                        p: 0,
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        '&:last-child': { pb: 0 },
                    }}
                >
                    {loading && !data ? (
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography sx={{ color: inkSecondary }}>Loading matrix…</Typography>
                        </Box>
                    ) : data && activeTab === 'by-role' ? (
                        <Box
                            className={isDark ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'}
                            sx={gridChromeSx}
                        >
                            <AgGridReact
                                columnDefs={roleColumnDefs}
                                rowData={rowData}
                                onGridReady={(params) => {
                                    setRoleGridApi(params.api);
                                    roleFilters.syncColumnFilters(params.api);
                                }}
                                onFilterChanged={(event) => roleFilters.syncColumnFilters(event.api)}
                                onCellValueChanged={handleCellValueChanged}
                                isExternalFilterPresent={isExternalFilterPresent}
                                doesExternalFilterPass={doesExternalFilterPass}
                                defaultColDef={{
                                    resizable: true,
                                    sortable: false,
                                }}
                                rowHeight={40}
                                headerHeight={48}
                                getRowId={(params) => params.data.id}
                                suppressRowTransform={true}
                                theme="legacy"
                            />
                        </Box>
                    ) : data && activeTab === 'by-user' ? (
                        <Box
                            className={isDark ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'}
                            sx={gridChromeSx}
                        >
                            <AgGridReact
                                columnDefs={userColumnDefs}
                                rowData={rowData}
                                onGridReady={(params) => {
                                    setUserGridApi(params.api);
                                    userFilters.syncColumnFilters(params.api);
                                }}
                                onFilterChanged={(event) => userFilters.syncColumnFilters(event.api)}
                                onCellValueChanged={handleCellValueChanged}
                                isExternalFilterPresent={isExternalFilterPresent}
                                doesExternalFilterPass={doesExternalFilterPass}
                                defaultColDef={{
                                    resizable: true,
                                    sortable: false,
                                    filter: false,
                                }}
                                rowHeight={40}
                                headerHeight={48}
                                getRowId={(params) => params.data.id}
                                suppressRowTransform={true}
                                theme="legacy"
                            />
                        </Box>
                    ) : null}
                </CardContent>
            </Card>

            {/* Permission Impact Preview Dialog */}
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: surfaceBg,
                        border: `1px solid ${panelBorder}`,
                        borderRadius: 1,
                        boxShadow: isDark
                            ? '0 8px 32px rgba(0,0,0,0.45)'
                            : '0 8px 32px rgba(15,33,52,0.12)',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: `1px solid ${panelBorder}`,
                        py: 1.5,
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        color: ink,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }} />
                        Permission Impact Preview
                    </Box>
                    <IconButton
                        onClick={() => setPreviewOpen(false)}
                        size="small"
                        sx={{ color: inkSecondary }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    {previewData && (
                        <>
                            <Typography variant="body2" sx={{ color: inkSecondary }} gutterBottom>
                                Changing{' '}
                                <strong style={{ color: ink }}>{previewData.role_name}</strong> (
                                {previewData.role_group} role) for{' '}
                                <strong style={{ color: ink }}>{previewData.feature_name}</strong>
                            </Typography>

                            <Box
                                sx={{
                                    my: 2,
                                    p: 1.5,
                                    borderRadius: 1,
                                    border: `1px solid ${panelBorder}`,
                                    bgcolor: isDark
                                        ? alpha(NAUTILUS.sand, 0.06)
                                        : alpha(NAUTILUS.deepOcean, 0.03),
                                    borderLeft: `3px solid ${NAUTILUS.sand}`,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontFamily: 'monospace',
                                        color: inkSecondary,
                                        display: 'block',
                                    }}
                                >
                                    {previewData.formula}
                                </Typography>
                            </Box>

                            {previewData.affected_users.length > 0 ? (
                                <List dense>
                                    {previewData.affected_users.map((user) => (
                                        <ListItem
                                            key={user.id}
                                            sx={{
                                                borderBottom: `1px solid ${alpha(panelBorder, 0.6)}`,
                                                px: 0,
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                {user.change === 'gain' ? (
                                                    <PersonAddIcon
                                                        sx={{ color: theme.palette.success.main }}
                                                    />
                                                ) : (
                                                    <PersonRemoveIcon
                                                        sx={{ color: theme.palette.error.main }}
                                                    />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={user.name}
                                                secondary={user.roles}
                                                primaryTypographyProps={{
                                                    sx: { color: ink, fontWeight: 600 },
                                                }}
                                                secondaryTypographyProps={{
                                                    sx: { color: inkSecondary },
                                                }}
                                            />
                                            <Chip
                                                label={
                                                    user.change === 'gain'
                                                        ? 'Will gain access'
                                                        : 'Will lose access'
                                                }
                                                size="small"
                                                sx={{
                                                    borderRadius: 1,
                                                    fontWeight: 600,
                                                    fontSize: '0.65rem',
                                                    letterSpacing: '0.04em',
                                                    textTransform: 'uppercase',
                                                    border: `1px solid ${panelBorder}`,
                                                    bgcolor:
                                                        user.change === 'gain'
                                                            ? alpha(theme.palette.success.main, 0.12)
                                                            : alpha(theme.palette.error.main, 0.12),
                                                    color:
                                                        user.change === 'gain'
                                                            ? theme.palette.success.main
                                                            : theme.palette.error.main,
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography
                                    variant="body2"
                                    sx={{ color: inkSecondary, textAlign: 'center', py: 2 }}
                                >
                                    No users will be affected by this change.
                                </Typography>
                            )}
                        </>
                    )}
                    {previewLoading && (
                        <Typography sx={{ color: inkSecondary, textAlign: 'center', py: 2 }}>
                            Loading impact…
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}` }}>
                    <Button onClick={() => setPreviewOpen(false)} sx={{ color: inkSecondary }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    severity={snackbar.severity}
                    sx={{ borderRadius: 1, border: `1px solid ${panelBorder}` }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FeaturePermissionsPage;
